import { Dialog, Transition } from '@headlessui/react'
import {
	ChartPieIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	DocumentTextIcon,
	PencilIcon,
	TrashIcon,
	UserGroupIcon,
	UsersIcon,
	XCircleIcon,
	XMarkIcon
} from '@heroicons/react/24/solid'
import React, {
	Fragment,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react'
import { dateToString } from '../../libs/date'

import { useHistory, useLocation } from 'react-router-dom'
import SearchAndCreateButton from '../../components/SearchAndCreateButton'
import Spin from '../../components/Spin'
import i18n from '../../i18n'
import { apiGet, apiPost, apiDel } from '../../libs/apiHelper'
import { useAppContext } from '../../libs/context'

// Componente helper para botones de acción de campaña
const CampaignActionButtons = ({
  campaign,
  history,
  openContactsModal,
  goToCampaignStats,
  closeCampaign,
  deleteCampaign,
  closingCampaignMap,
  deletingCampaignMap
}) => {
  const isClosing = closingCampaignMap[campaign.campaignId]
  const isDeleting = deletingCampaignMap[campaign.campaignId]
  const isOpen = campaign.status === 'open'

  return (
    <div className="flex items-center gap-1 ml-auto">
      <button
        onClick={() => {
          history.push(`/campaign?campaignId=${campaign.campaignId}`)
        }}
        className="flex-shrink-0 text-indigo-600 flex items-center justify-center w-9 h-9 rounded hover:bg-indigo-50 hover:text-indigo-800 transition-colors"
        title="Editar campaña"
      >
        <PencilIcon className="h-5 w-5" />
      </button>
      <button
        onClick={() => openContactsModal(campaign.campaignId)}
        className="flex-shrink-0 text-blue-600 flex items-center justify-center w-9 h-9 rounded hover:bg-blue-50 hover:text-blue-800 transition-colors"
        title="Ver contactos"
      >
        <UserGroupIcon className="h-5 w-5" />
      </button>
      <button
        onClick={() => goToCampaignStats(campaign.campaignId)}
        className="flex-shrink-0 text-blue-800 flex items-center justify-center w-9 h-9 rounded hover:bg-blue-50 hover:text-indigo-600 transition-colors"
        title="Ver estadísticas"
      >
        <ChartPieIcon className="h-6 w-6 align-center" />
      </button>
      {isOpen && (
        <button
          onClick={() => closeCampaign(campaign.campaignId)}
          disabled={isClosing}
          className="flex-shrink-0 text-amber-600 flex items-center justify-center w-9 h-9 rounded hover:bg-amber-50 hover:text-amber-800 transition-colors disabled:opacity-50"
          title="Cerrar campaña"
        >
          <XCircleIcon className="h-5 w-5" />
        </button>
      )}
      <button
        onClick={() => deleteCampaign(campaign.campaignId)}
        disabled={isDeleting}
        className="flex-shrink-0 text-red-600 flex items-center justify-center w-9 h-9 rounded hover:bg-red-50 hover:text-red-800 transition-colors disabled:opacity-50"
        title="Eliminar campaña"
      >
        <TrashIcon className="h-5 w-5" />
      </button>
    </div>
  )
}

const Campaigns = () => {
  const { userInfo } = useAppContext()
  const history = useHistory()
  const location = useLocation()
  const [campaigns, setCampaigns] = useState([])
  const [scheduled, setScheduled] = useState([])
  // Campañas programadas REALES (items de prod-scheduled vía GET /schedules).
  // La lista `scheduled` de arriba deriva de prod-campaigns (timestamp futuro) y
  // nunca mostraba los schedules; esta sí.
  const [realSchedules, setRealSchedules] = useState([])
  const [cancelingSchedule, setCancelingSchedule] = useState(null)
  const [allCampaigns, setAllCampaigns] = useState([]) // Todas las campañas para procesar

  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [nextKey, setNextKey] = useState(null)
  const [groupByTemplate, setGroupByTemplate] = useState(() => {
    try {
      const storedValue = sessionStorage.getItem('campaignsGroupByTemplate')
      if (storedValue === 'true') return true
      if (storedValue === 'false') return false
    } catch (error) {
      console.warn('[Campaigns] Unable to read view mode', error)
    }
    return true
  }) // Por defecto agrupado
  const [groupedCampaignsData, setGroupedCampaignsData] = useState(null) // Datos agrupados del backend (lanzadas)
  const [groupedScheduledData, setGroupedScheduledData] = useState(null) // Datos agrupados programadas
  const [filterText, setFilterText] = useState('') // Texto de filtro
  const [filterDebounceTimer, setFilterDebounceTimer] = useState(null) // Timer para debounce del filtro
  const [campaignTypeFilter, setCampaignTypeFilter] = useState('all') // 'all', 'launched', 'scheduled'
  const [collapsedTemplates, setCollapsedTemplates] = useState(new Set()) // Templates colapsados por nombre
  const [contactsModalOpen, setContactsModalOpen] = useState(false) // Estado del modal de contactos
  const [selectedCampaignId, setSelectedCampaignId] = useState(null) // ID de la campaña seleccionada para ver contactos
  const [campaignContacts, setCampaignContacts] = useState([]) // Lista de contactos de la campaña
  const [loadingContacts, setLoadingContacts] = useState(false) // Estado de carga de contactos
  const [updatingFlowMap, setUpdatingFlowMap] = useState({}) // Estado de cierre/reapertura por teléfono
  const [deletingCampaignMap, setDeletingCampaignMap] = useState({}) // Estado de eliminación por campaña
  const [closingCampaignMap, setClosingCampaignMap] = useState({}) // Estado de cierre por campaña
  const scrollPositionRef = useRef(null) // Ref para preservar posición del scroll durante paginación
  const isRestoringScrollRef = useRef(false) // Flag para evitar loops infinitos
  const hasRestoredScrollRef = useRef(false)
  const pendingScrollRestoreRef = useRef(null)
  const scrollRestoreTimerRef = useRef(null)
  const scrollStorageKey = 'campaignsScrollY'
  const pathStorageKey = 'campaignsPath'
  const viewModeStorageKey = 'campaignsGroupByTemplate'

  const goToCampaignStats = useCallback(
    (campaignId) => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      const campaignsPath = `${history.location.pathname}${history.location.search}`
      try {
        sessionStorage.setItem(scrollStorageKey, String(scrollTop))
        sessionStorage.setItem(pathStorageKey, campaignsPath)
        sessionStorage.setItem(viewModeStorageKey, String(groupByTemplate))
      } catch (error) {
        console.warn('[Campaigns] Unable to store scroll position', error)
      }
      history.push({
        pathname: '/statistics',
        search: `?campaignId=${campaignId}`,
        state: {
          fromCampaigns: true,
          campaignsPath,
          campaignsScrollY: scrollTop
        }
      })
    },
    [groupByTemplate, history]
  )

  // Abre las estadísticas agregadas de toda una template (todas sus campañas
  // dentro del rango de fechas seleccionado en la vista Statistics).
  const goToTemplateStats = useCallback(
    (templateName) => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      const campaignsPath = `${history.location.pathname}${history.location.search}`
      try {
        sessionStorage.setItem(scrollStorageKey, String(scrollTop))
        sessionStorage.setItem(pathStorageKey, campaignsPath)
        sessionStorage.setItem(viewModeStorageKey, String(groupByTemplate))
      } catch (error) {
        console.warn('[Campaigns] Unable to store scroll position', error)
      }
      history.push({
        pathname: '/statistics',
        search: `?template=${encodeURIComponent(templateName)}`,
        state: {
          fromCampaigns: true,
          campaignsPath,
          campaignsScrollY: scrollTop
        }
      })
    },
    [groupByTemplate, history]
  )

  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current) {
      return
    }

    const restoreScrollY = location.state?.restoreScrollY
    if (typeof restoreScrollY === 'number') {
      scrollPositionRef.current = restoreScrollY
      isRestoringScrollRef.current = true
      pendingScrollRestoreRef.current = restoreScrollY
      hasRestoredScrollRef.current = true
      return
    }

    const storedScrollY = sessionStorage.getItem(scrollStorageKey)
    const storedPath = sessionStorage.getItem(pathStorageKey)
    const currentPath = `${location.pathname}${location.search}`

    if (storedScrollY && storedPath === currentPath) {
      const parsedScrollY = Number(storedScrollY)
      if (!Number.isNaN(parsedScrollY)) {
        scrollPositionRef.current = parsedScrollY
        isRestoringScrollRef.current = true
        pendingScrollRestoreRef.current = parsedScrollY
      }
      sessionStorage.removeItem(scrollStorageKey)
      sessionStorage.removeItem(pathStorageKey)
      hasRestoredScrollRef.current = true
    }
  }, [history, location])

  useEffect(() => {
    try {
      sessionStorage.setItem(viewModeStorageKey, String(groupByTemplate))
    } catch (error) {
      console.warn('[Campaigns] Unable to store view mode', error)
    }
  }, [groupByTemplate])

  useEffect(() => {
    if (pendingScrollRestoreRef.current === null) {
      return
    }
    if (loadingCampaigns || loadingMore) {
      return
    }

    const targetScrollY = pendingScrollRestoreRef.current
    const maxAttempts = 12
    let attempts = 0

    const attemptRestore = () => {
      if (pendingScrollRestoreRef.current === null) {
        return
      }
      const scrollHeight =
        document.documentElement.scrollHeight || document.body.scrollHeight || 0

      attempts += 1
      if (scrollHeight >= targetScrollY || attempts >= maxAttempts) {
        window.scrollTo(0, targetScrollY)
        pendingScrollRestoreRef.current = null
        if (scrollRestoreTimerRef.current) {
          clearTimeout(scrollRestoreTimerRef.current)
          scrollRestoreTimerRef.current = null
        }
        return
      }

      scrollRestoreTimerRef.current = setTimeout(attemptRestore, 120)
    }

    attemptRestore()

    return () => {
      if (scrollRestoreTimerRef.current) {
        clearTimeout(scrollRestoreTimerRef.current)
        scrollRestoreTimerRef.current = null
      }
    }
  }, [
    campaigns,
    scheduled,
    groupedCampaignsData,
    groupedScheduledData,
    loadingCampaigns,
    loadingMore
  ])

  // Función para toggle de template colapsado
  const toggleTemplateCollapse = useCallback((templateName) => {
    setCollapsedTemplates((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(templateName)) {
        newSet.delete(templateName)
      } else {
        newSet.add(templateName)
      }
      return newSet
    })
  }, [])

  // Función para abrir el modal de contactos y cargar los datos
  const openContactsModal = useCallback(async (campaignId) => {
    setSelectedCampaignId(campaignId)
    setContactsModalOpen(true)
    setLoadingContacts(true)
    setCampaignContacts([])

    try {
      const response = await apiGet(`/campaigns/${campaignId}/contacts`)
      if (response.contacts) {
        setCampaignContacts(response.contacts)
      }
    } catch (error) {
      console.error('[Campaigns] Error loading contacts:', error)
      setCampaignContacts([])
    } finally {
      setLoadingContacts(false)
    }
  }, [])

  const updateContactFlowStatus = useCallback(
    async (phone, closed, campaignIdOverride = null) => {
      const targetCampaignId = campaignIdOverride || selectedCampaignId
      if (!targetCampaignId || !phone) {
        console.warn(
          '[Campaigns] Missing campaignId or phone for flow status update',
          {
            targetCampaignId,
            phone
          }
        )
        return
      }

      setUpdatingFlowMap((prev) => ({ ...prev, [phone]: true }))
      try {
        // Optimistic UI update so the action is visible immediately.
        setCampaignContacts((prevContacts) =>
          prevContacts.map((contact) => {
            if (String(contact.phone) !== String(phone)) return contact
            return {
              ...contact,
              flowClosed: !!closed,
              flowCompleted: !!closed || !!contact.flowCompleted,
              canCloseFlow: !closed && !!contact.hasDelivery
            }
          })
        )

        await apiPost(`/campaigns/${targetCampaignId}/contacts/flow`, {
          body: { phone, closed }
        })

        const refreshed = await apiGet(`/campaigns/${targetCampaignId}/contacts`)
        if (refreshed.contacts) {
          setCampaignContacts(refreshed.contacts)
        }
      } catch (error) {
        console.error('[Campaigns] Error updating contact flow status:', error)
      } finally {
        setUpdatingFlowMap((prev) => ({ ...prev, [phone]: false }))
      }
    },
    [selectedCampaignId]
  )

  // Cargar las campañas programadas reales (prod-scheduled)
  const loadSchedules = useCallback(async () => {
    try {
      const res = await apiGet('/schedules')
      setRealSchedules(Array.isArray(res) ? res : res?.items || [])
    } catch (err) {
      console.warn('[Campaigns] No se pudieron cargar los schedules', err)
    }
  }, [])

  // Cancelar (borrar) una campaña programada
  const cancelSchedule = useCallback(async (scheduleId) => {
    setCancelingSchedule(scheduleId)
    try {
      await apiDel(`/schedule/${scheduleId}`)
      setRealSchedules((prev) =>
        prev.filter((s) => s.scheduleId !== scheduleId)
      )
    } catch (err) {
      console.error('[Campaigns] Error cancelando schedule', err)
      alert('No se pudo cancelar la programación')
    } finally {
      setCancelingSchedule(null)
    }
  }, [])

  // Cargar los schedules reales al montar
  useEffect(() => {
    loadSchedules()
  }, [loadSchedules])

  // Función para cerrar una campaña (cambiar status a 'close')
  const closeCampaign = useCallback(async (campaignId) => {
    setClosingCampaignMap((prev) => ({ ...prev, [campaignId]: true }))
    try {
      await apiPost(`/close-campaign/${campaignId}`)
      // Actualizar estado local
      setCampaigns((prev) =>
        prev.map((c) =>
          c.campaignId === campaignId ? { ...c, status: 'close' } : c
        )
      )
      setAllCampaigns((prev) =>
        prev.map((c) =>
          c.campaignId === campaignId ? { ...c, status: 'close' } : c
        )
      )
      // Actualizar datos agrupados si existen
      setGroupedCampaignsData((prev) => {
        if (!prev) return prev
        return prev.map((group) => ({
          ...group,
          campaigns: group.campaigns.map((c) =>
            c.campaignId === campaignId ? { ...c, status: 'close' } : c
          )
        }))
      })
      setGroupedScheduledData((prev) => {
        if (!prev) return prev
        return prev.map((group) => ({
          ...group,
          campaigns: group.campaigns.map((c) =>
            c.campaignId === campaignId ? { ...c, status: 'close' } : c
          )
        }))
      })
    } catch (error) {
      console.error('[Campaigns] Error closing campaign:', error)
      alert('Error al cerrar la campaña')
    } finally {
      setClosingCampaignMap((prev) => ({ ...prev, [campaignId]: false }))
    }
  }, [])

  // Estado del modal de confirmación de borrado (soft-delete).
  // Guardamos la campaña completa para poder mostrar su nombre en el modal.
  const [campaignPendingDelete, setCampaignPendingDelete] = useState(null)

  // Ahora sólo abre el modal — la ejecución del borrado la hace `confirmDeleteCampaign`.
  const deleteCampaign = useCallback((campaignId) => {
    // Buscar la campaña en el estado para mostrar contexto (nombre, template).
    const match =
      allCampaigns.find((c) => c.campaignId === campaignId) ||
      campaigns.find((c) => c.campaignId === campaignId) ||
      scheduled.find((c) => c.campaignId === campaignId) || {
        campaignId
      }
    setCampaignPendingDelete(match)
  }, [allCampaigns, campaigns, scheduled])

  // Ejecuta el soft-delete tras confirmar en el modal.
  const confirmDeleteCampaign = useCallback(async () => {
    if (!campaignPendingDelete) return
    const campaignId = campaignPendingDelete.campaignId
    setCampaignPendingDelete(null)
    setDeletingCampaignMap((prev) => ({ ...prev, [campaignId]: true }))
    try {
      await apiDel(`/remove-campaign/${campaignId}`)
      // Eliminar de todos los estados
      setCampaigns((prev) => prev.filter((c) => c.campaignId !== campaignId))
      setScheduled((prev) => prev.filter((c) => c.campaignId !== campaignId))
      setAllCampaigns((prev) => prev.filter((c) => c.campaignId !== campaignId))
      // Actualizar datos agrupados
      setGroupedCampaignsData((prev) => {
        if (!prev) return prev
        const updated = prev
          .map((group) => ({
            ...group,
            campaigns: group.campaigns.filter((c) => c.campaignId !== campaignId)
          }))
          .filter((group) => group.campaigns.length > 0)
        return updated
      })
      setGroupedScheduledData((prev) => {
        if (!prev) return prev
        const updated = prev
          .map((group) => ({
            ...group,
            campaigns: group.campaigns.filter((c) => c.campaignId !== campaignId)
          }))
          .filter((group) => group.campaigns.length > 0)
        return updated
      })
    } catch (error) {
      console.error('[Campaigns] Error deleting campaign:', error)
      alert('Error al eliminar la campaña')
    } finally {
      setDeletingCampaignMap((prev) => ({ ...prev, [campaignId]: false }))
    }
  }, [campaignPendingDelete])

  // Ref para prevenir múltiples peticiones simultáneas
  const isRequestingRef = useRef(false)

  // Función para procesar campañas y separarlas en lanzadas y programadas
  const processCampaigns = useCallback((newCampaigns) => {
    if (!newCampaigns || newCampaigns.length === 0) return

    // Agregar nuevas campañas a todas las existentes
    setAllCampaigns((prev) => {
      const combined = [...prev, ...newCampaigns]
      // Eliminar duplicados por campaignId
      const unique = combined.filter(
        (campaign, index, self) =>
          index === self.findIndex((c) => c.campaignId === campaign.campaignId)
      )

      // Ordenar por timestamp descendente (más recientes primero)
      unique.sort((a, b) => {
        const dateA = new Date(a.timestamp || 0)
        const dateB = new Date(b.timestamp || 0)
        return dateB - dateA
      })

      // Separar en lanzadas y programadas
      const now = new Date()
      const launched = []
      const scheduled = []

      unique.forEach((campaign) => {
        const campaignDate = new Date(campaign.timestamp)
        if (campaignDate < now) {
          launched.push(campaign)
        } else {
          scheduled.push(campaign)
        }
      })

      setCampaigns(launched)
      setScheduled(scheduled)

      return unique
    })
  }, [])

  async function listCampaigns(isInitial = true) {
    // Prevenir múltiples peticiones simultáneas
    if (!isInitial) {
      // Verificar condiciones ANTES de hacer cualquier cosa
      // Ser más permisivo: si hay nextKey, intentar cargar (el backend decidirá si hay más)

      // Si no hay nextKey, no hay más contenido - resetear y salir
      if (!nextKey || nextKey === '') {
        console.log('[Campaigns] ⏭️ Saltando carga - no hay nextKey')
        setHasMore(false)
        setNextKey(null)
        isRequestingRef.current = false // Asegurar que el flag esté reseteado
        return
      }

      // Verificar si ya está cargando
      if (isRequestingRef.current || loadingMore || loadingCampaigns) {
        console.log('[Campaigns] ⏭️ Saltando carga - ya está en proceso:', {
          isRequesting: isRequestingRef.current,
          loadingMore,
          loadingCampaigns,
          hasNextKey: !!nextKey,
          nextKey: nextKey ? nextKey.substring(0, 30) + '...' : null
        })
        // Si el flag está bloqueado pero no está realmente cargando, resetearlo después de un delay
        if (isRequestingRef.current && !loadingMore && !loadingCampaigns) {
          console.warn(
            '[Campaigns] ⚠️ Flag isRequesting bloqueado pero no está cargando - reseteando después de 1s'
          )
          setTimeout(() => {
            if (isRequestingRef.current && !loadingMore && !loadingCampaigns) {
              console.log('[Campaigns] 🔓 Reseteando flag bloqueado')
              isRequestingRef.current = false
            }
          }, 1000)
        }
        return
      }

      // Establecer flags INMEDIATAMENTE para prevenir otras llamadas
      console.log('[Campaigns] 🔒 Estableciendo flag isRequesting a true')
      isRequestingRef.current = true
      setLoadingMore(true)
    } else {
      // En carga inicial, resetear todo
      isRequestingRef.current = false // Asegurar que no hay bloqueo
      // Solo mostrar loading spinner en la carga inicial real (primera vez)
      // NO mostrar cuando solo cambia el filtro para evitar que parezca un refresh
      const trimmedFilter = filterText ? filterText.trim() : ''
      const isFilterChange =
        trimmedFilter && (allCampaigns.length > 0 || campaigns.length > 0)

      if (!isFilterChange) {
        // Solo mostrar loading si NO es un cambio de filtro
        setLoadingCampaigns(true)
      }

      // Resetear datos solo si hay datos previos o si no hay filtro
      // También resetear si hay datos agrupados (cambio de modo de vista)
      const hasListData =
        allCampaigns.length > 0 ||
        campaigns.length > 0 ||
        scheduled.length > 0
      const hasGroupedData =
        (groupedCampaignsData && groupedCampaignsData.length > 0) ||
        (groupedScheduledData && groupedScheduledData.length > 0)

      if (!trimmedFilter || hasListData || hasGroupedData) {
        setAllCampaigns([])
        setCampaigns([])
        setScheduled([])
        setGroupedCampaignsData(null) // Reset datos agrupados lanzadas
        setGroupedScheduledData(null) // Reset datos agrupados programadas
      }
      setHasMore(true) // Reset hasMore en carga inicial
      setNextKey(null) // Reset nextKey en carga inicial
    }

    // Log del contexto del usuario
    if (isInitial) {
      console.log('[Campaigns] Información del usuario del contexto:')
      console.log('[Campaigns] userInfo:', userInfo)
      console.log('[Campaigns] userId:', userInfo?.userId)
      console.log('[Campaigns] email:', userInfo?.email)
      console.log(
        '[Campaigns] impersonatedUserId:',
        userInfo?.impersonatedUserId
      )
      console.log('[Campaigns] impersonatedEmail:', userInfo?.impersonatedEmail)
    }

    try {
      // Usar el helper que automáticamente incluye el token de impersonación si está disponible
      console.log('[Campaigns] Cargando campañas...', {
        isInitial,
        hasNextKey: !!nextKey
      })

      // Construir query parameters manualmente
      const queryParams = new URLSearchParams()
      queryParams.append('limit', '60')

      if (!isInitial && nextKey) {
        queryParams.append('lastKey', nextKey)
        console.log(
          '[Campaigns] Usando nextKey para paginación:',
          nextKey.substring(0, 50) + '...'
        )
      }

      // Agregar parámetro de agrupación si está activado
      if (groupByTemplate) {
        queryParams.append('groupByTemplate', 'true')
      }

      // Agregar filtro si existe (se envía a la API para filtrar en DB)
      const trimmedFilter = filterText ? filterText.trim() : ''
      if (trimmedFilter) {
        queryParams.append('filter', trimmedFilter)
        // Cuando hay filtro activo, aumentar el límite para obtener más resultados antes de filtrar
        // El backend tiene un máximo de 100, así que usamos ese límite máximo
        queryParams.set('limit', '100')
        // IMPORTANTE: Cuando hay filtro activo, NO hacer paginación porque:
        // 1. El filtro se aplica DESPUÉS de obtener los resultados de DynamoDB
        // 2. Si paginamos, puede que la siguiente página no tenga resultados que cumplan el filtro
        // 3. Esto causaría que desaparezcan todos los resultados
        if (!isInitial) {
          console.log(
            '[Campaigns] ⚠️ Filtro activo detectado durante paginación - cancelando'
          )
          setHasMore(false) // Marcar que no hay más para evitar intentos de paginación
          setNextKey(null)
          return // Salir temprano porque no deberíamos estar paginando con filtro activo
        }
        console.log(
          '[Campaigns] 🔍 Enviando filtro a la API:',
          trimmedFilter,
          '- Límite: 100',
          '- Sin paginación'
        )
      } else {
        console.log('[Campaigns] Sin filtro, cargando todas las campañas')
      }

      // Construir URL con query parameters
      const url = `/campaigns?${queryParams.toString()}`

      console.log('[Campaigns] 📤 Enviando petición a API:', {
        url,
        filterText: filterText || '(vacío)',
        queryString: queryParams.toString()
      })

      const response = await apiGet(url)

      console.log('[Campaigns] 📥 Respuesta recibida de API:', {
        hasResponse: !!response,
        isArray: Array.isArray(response),
        hasGrouped: response?.grouped,
        hasCampaigns: !!response?.campaigns,
        templatesCount: response?.templates?.length,
        campaignsCount: Array.isArray(response)
          ? response.length
          : response?.campaigns?.length
      })

      // Adaptar a nueva estructura de respuesta
      let campaignsData = []
      let paginationData = null
      let isGrouped = false

      if (Array.isArray(response)) {
        // Compatibilidad con formato antiguo (si aún no está desplegado el nuevo backend)
        console.warn('[Campaigns] Respuesta en formato antiguo (array)')
        campaignsData = response
        setHasMore(false)
        setNextKey(null)
      } else if (response.grouped && response.templates) {
        // Formato agrupado por template
        isGrouped = true
        // Para la vista agrupada, necesitamos aplanar las campañas para procesarlas
        campaignsData = response.templates.flatMap((group) => group.campaigns)
        paginationData = response.pagination

        // IMPORTANTE: Si hay filtro activo, NO permitir más paginación
        const trimmedFilter = filterText ? filterText.trim() : ''
        const apiHasMore =
          paginationData?.hasMore === true || paginationData?.hasMore === 'true'
        const apiNextKey = paginationData?.nextKey || null

        const newHasMore = trimmedFilter ? false : apiHasMore && !!apiNextKey
        const newNextKey = trimmedFilter ? null : apiNextKey

        console.log('[Campaigns] 📄 Respuesta agrupada:', {
          templatesCount: paginationData.templatesCount,
          campaignsCount: campaignsData.length,
          hasMore: newHasMore,
          hasNextKey: !!newNextKey,
          filterActive: !!trimmedFilter,
          templatesReceived: response.templates.length,
          sampleTemplates: response.templates.slice(0, 2).map((t) => ({
            templateName: t.template.name,
            campaignsCount: t.campaigns.length
          }))
        })

        // Actualizar estado de paginación
        if (!newNextKey || newNextKey === '') {
          setHasMore(false)
          setNextKey(null)
        } else {
          setHasMore(newHasMore)
          setNextKey(newNextKey)
        }

        // NO guardar aquí, se guardará después de procesar en el bloque siguiente
      } else if (response.campaigns) {
        // Nuevo formato con paginación (vista normal)
        campaignsData = response.campaigns
        paginationData = response.pagination

        // Actualizar estado de paginación
        // IMPORTANTE: Si hay filtro activo, NO permitir más paginación
        const trimmedFilter = filterText ? filterText.trim() : ''
        const apiHasMore =
          paginationData?.hasMore === true || paginationData?.hasMore === 'true'
        const apiNextKey = paginationData?.nextKey || null

        const newHasMore = trimmedFilter ? false : apiHasMore && !!apiNextKey
        const newNextKey = trimmedFilter ? null : apiNextKey

        console.log('[Campaigns] 📊 Actualizando paginación (lista):', {
          apiHasMore,
          apiNextKey: apiNextKey ? apiNextKey.substring(0, 30) + '...' : null,
          newHasMore,
          newNextKey: newNextKey ? newNextKey.substring(0, 30) + '...' : null,
          filterActive: !!trimmedFilter
        })

        // Si no hay nextKey, no hay más contenido
        if (!newNextKey || newNextKey === '') {
          setHasMore(false)
          setNextKey(null)
        } else {
          setHasMore(newHasMore)
          setNextKey(newNextKey)
        }

        console.log('[Campaigns] 📄 Paginación actualizada:', {
          count: paginationData.count,
          hasMore: newHasMore,
          hasNextKey: !!newNextKey,
          nextKey: newNextKey ? newNextKey.substring(0, 50) + '...' : null,
          campaignsReceived: campaignsData.length
        })

        // Limpiar datos agrupados si cambiamos a vista normal
        setGroupedCampaignsData(null)
      } else {
        console.error('[Campaigns] Formato de respuesta desconocido:', response)
        campaignsData = []
      }

      console.log('[Campaigns] Campañas cargadas:', {
        count: campaignsData.length,
        filterText: filterText || '(sin filtro)',
        isGrouped,
        isInitial,
        hasMoreFromAPI: paginationData?.hasMore,
        hasNextKeyFromAPI: !!paginationData?.nextKey,
        sampleCampaign: campaignsData[0]
          ? {
              campaignId: campaignsData[0].campaignId,
              flow: campaignsData[0].flow,
              campaignName: campaignsData[0].campaignName
            }
          : null
      })

      // Si es paginación y recibimos 0 campañas, verificar si realmente no hay más
      // El backend debería indicar si hay más contenido en paginationData
      if (!isInitial && campaignsData.length === 0) {
        console.log('[Campaigns] ⚠️ Paginación retornó 0 campañas')

        // Verificar si la API dice que hay más contenido
        const apiSaysHasMore =
          paginationData?.hasMore === true || paginationData?.hasMore === 'true'
        const apiHasNextKey =
          paginationData?.nextKey && paginationData.nextKey !== ''

        if (!apiSaysHasMore && !apiHasNextKey) {
          console.log('[Campaigns] ✅ Backend confirma: no hay más contenido')
          setHasMore(false)
          setNextKey(null)
          // IMPORTANTE: Resetear flag antes de retornar
          isRequestingRef.current = false
          // No procesar nada más si no hay datos y el backend confirma que no hay más
          return
        } else {
          console.log(
            '[Campaigns] ⚠️ Backend dice que hay más pero retornó 0 campañas - continuando...'
          )
          // Continuar procesando aunque haya 0 campañas, puede que el backend esté procesando
        }
      }

      if (campaignsData.length > 0) {
        // Si viene agrupado, procesar las campañas pero mantener la estructura agrupada
        if (isGrouped && response.templates && response.templates.length > 0) {
          // Usar response.templates directamente en lugar del estado anterior
          const templatesToProcess = response.templates

          console.log('[Campaigns] 📊 Procesando grupos recibidos:', {
            totalGroups: templatesToProcess.length,
            filterText: filterText || '(sin filtro)',
            sampleGroups: templatesToProcess.slice(0, 2).map((g) => ({
              templateName: g.template.name,
              campaignsCount: g.campaigns.length
            }))
          })

          // Separar campañas lanzadas y programadas dentro de cada grupo
          const now = new Date()
          const groupedLaunched = []
          const groupedScheduled = []

          templatesToProcess.forEach((group) => {
            const launched = []
            const scheduled = []

            group.campaigns.forEach((campaign) => {
              const campaignDate = new Date(campaign.timestamp)
              if (campaignDate < now) {
                launched.push(campaign)
              } else {
                scheduled.push(campaign)
              }
            })

            if (launched.length > 0) {
              groupedLaunched.push({
                ...group,
                campaigns: launched
              })
            }

            if (scheduled.length > 0) {
              groupedScheduled.push({
                ...group,
                campaigns: scheduled
              })
            }
          })

          console.log('[Campaigns] ✅ Grupos procesados:', {
            launchedGroups: groupedLaunched.length,
            scheduledGroups: groupedScheduled.length,
            totalCampaigns:
              groupedLaunched.reduce((sum, g) => sum + g.campaigns.length, 0) +
              groupedScheduled.reduce((sum, g) => sum + g.campaigns.length, 0)
          })

          // Guardar datos agrupados separados
          if (isInitial) {
            // Carga inicial: reemplazar todo
            setGroupedCampaignsData(groupedLaunched)
            setGroupedScheduledData(groupedScheduled)
          } else {
            // Paginación: agregar las nuevas campañas a los templates existentes
            setGroupedCampaignsData((prev) => {
              if (!prev || prev.length === 0) return groupedLaunched

              // Crear un mapa de templates existentes por nombre para acceso rápido
              const existingTemplatesMap = new Map()
              prev.forEach((group) => {
                existingTemplatesMap.set(group.template.name, group)
              })

              // Crear un Set de nombres de templates recibidos para identificar los nuevos
              const receivedTemplateNames = new Set(groupedLaunched.map((g) => g.template.name))

              // Actualizar templates existentes con nuevas campañas
              const updatedExistingTemplates = prev.map((existingGroup) => {
                // Buscar si este template viene en la respuesta nueva
                const newGroup = groupedLaunched.find(
                  (g) => g.template.name === existingGroup.template.name
                )

                if (newGroup) {
                  // Template existe: agregar nuevas campañas sin duplicados
                  const existingCampaignIds = new Set(
                    existingGroup.campaigns.map((c) => c.campaignId)
                  )
                  const newCampaigns = newGroup.campaigns.filter(
                    (campaign) => !existingCampaignIds.has(campaign.campaignId)
                  )

                  return {
                    ...existingGroup,
                    campaigns: [...existingGroup.campaigns, ...newCampaigns]
                  }
                } else {
                  // Template no viene en la respuesta nueva: mantenerlo tal cual
                  return existingGroup
                }
              })

              // Agregar templates completamente nuevos al final
              const completelyNewTemplates = groupedLaunched.filter(
                (newGroup) => !existingTemplatesMap.has(newGroup.template.name)
              )

              const result = [...updatedExistingTemplates, ...completelyNewTemplates]

              console.log(
                '[Campaigns] ➕ Agregando campañas a templates existentes (lanzadas)',
                {
                  templatesExistentes: prev.length,
                  templatesActualizados: updatedExistingTemplates.length,
                  templatesNuevos: completelyNewTemplates.length,
                  templatesFinales: result.length,
                  totalCampaignsAntes: prev.reduce((sum, g) => sum + g.campaigns.length, 0),
                  totalCampaignsDespues: result.reduce((sum, g) => sum + g.campaigns.length, 0)
                }
              )

              return result
            })

            setGroupedScheduledData((prev) => {
              if (!prev || prev.length === 0) return groupedScheduled

              // Crear un mapa de templates existentes por nombre para acceso rápido
              const existingTemplatesMap = new Map()
              prev.forEach((group) => {
                existingTemplatesMap.set(group.template.name, group)
              })

              // Crear un Set de nombres de templates recibidos para identificar los nuevos
              const receivedTemplateNames = new Set(groupedScheduled.map((g) => g.template.name))

              // Actualizar templates existentes con nuevas campañas
              const updatedExistingTemplates = prev.map((existingGroup) => {
                // Buscar si este template viene en la respuesta nueva
                const newGroup = groupedScheduled.find(
                  (g) => g.template.name === existingGroup.template.name
                )

                if (newGroup) {
                  // Template existe: agregar nuevas campañas sin duplicados
                  const existingCampaignIds = new Set(
                    existingGroup.campaigns.map((c) => c.campaignId)
                  )
                  const newCampaigns = newGroup.campaigns.filter(
                    (campaign) => !existingCampaignIds.has(campaign.campaignId)
                  )

                  return {
                    ...existingGroup,
                    campaigns: [...existingGroup.campaigns, ...newCampaigns]
                  }
                } else {
                  // Template no viene en la respuesta nueva: mantenerlo tal cual
                  return existingGroup
                }
              })

              // Agregar templates completamente nuevos al final
              const completelyNewTemplates = groupedScheduled.filter(
                (newGroup) => !existingTemplatesMap.has(newGroup.template.name)
              )

              const result = [...updatedExistingTemplates, ...completelyNewTemplates]

              console.log(
                '[Campaigns] ➕ Agregando campañas a templates existentes (programadas)',
                {
                  templatesExistentes: prev.length,
                  templatesActualizados: updatedExistingTemplates.length,
                  templatesNuevos: completelyNewTemplates.length,
                  templatesFinales: result.length,
                  totalCampaignsAntes: prev.reduce((sum, g) => sum + g.campaigns.length, 0),
                  totalCampaignsDespues: result.reduce((sum, g) => sum + g.campaigns.length, 0)
                }
              )

              return result
            })
          }

          // NO procesar normalmente cuando está agrupado - ya tenemos los datos agrupados
          // Solo procesar si NO está agrupado para mantener compatibilidad con la vista lista
          // processCampaigns(campaignsData) // Comentado para evitar duplicados cuando está agrupado
        } else {
          // Vista normal (no agrupada)
          processCampaigns(campaignsData)
        }
      } else {
        // Si no hay campañas en esta página
        const trimmedFilter = filterText ? filterText.trim() : ''
        if (trimmedFilter) {
          // Si hay filtro activo y no hay resultados, limpiar todo
          console.log(
            '[Campaigns] 🔍 No se encontraron campañas con el filtro:',
            trimmedFilter
          )
          // Limpiar todos los estados cuando no hay resultados con filtro
          setCampaigns([])
          setScheduled([])
          setGroupedCampaignsData([])
          setGroupedScheduledData([])
          setHasMore(false)
          setNextKey(null)
        } else {
          console.log('[Campaigns] ⛔ No hay campañas para mostrar')
          if (!isInitial) {
            // Si es paginación y no hay campañas, significa que no hay más contenido
            console.log(
              '[Campaigns] ⛔ No hay más campañas para cargar (paginación vacía)'
            )
            setHasMore(false)
            setNextKey(null)
          } else {
            // Si es la carga inicial y no hay campañas, limpiar estados
            setCampaigns([])
            setScheduled([])
            setHasMore(false)
            setNextKey(null)
            if (groupByTemplate) {
              setGroupedCampaignsData([])
              setGroupedScheduledData([])
            }
          }
        }
      }
    } catch (error) {
      console.error('[Campaigns] Error cargando campañas:', error)
      // En caso de error, marcar que no hay más para evitar loops infinitos
      if (!isInitial) {
        setHasMore(false)
        setNextKey(null)
      }
      // IMPORTANTE: Resetear flag incluso en caso de error
      isRequestingRef.current = false
    } finally {
      // IMPORTANTE: Resetear flag SIEMPRE, incluso si hay errores o returns tempranos
      isRequestingRef.current = false
      setLoadingCampaigns(false)
      setLoadingMore(false)
      console.log('[Campaigns] 🔓 Flag isRequesting reseteado en finally', {
        wasInitial: isInitial,
        hasMore,
        hasNextKey: !!nextKey
      })
    }
  }

  // Infinite scroll basado en scroll - Detecta cuando se llega al 85% del scroll
  useEffect(() => {
    const handleScroll = () => {
      // NO hacer paginación si hay un filtro activo
      const hasActiveFilter = filterText && filterText.trim()
      if (hasActiveFilter) {
        return // No hacer scroll infinito cuando hay filtro activo
      }

      // Calcular el porcentaje de scroll
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = window.innerHeight
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight

      // Si se ha hecho scroll al 85% o más y hay nextKey disponible
      // Siempre intentar cargar si hay nextKey - el backend decidirá si hay más contenido
      const hasValidNextKey =
        nextKey && nextKey !== null && nextKey !== undefined && nextKey !== ''

      // Solo verificar que no esté cargando actualmente (pero ser más permisivo)
      // Si hay nextKey, siempre intentar cargar - el backend cortará si no hay más
      const canLoadMore =
        hasValidNextKey &&
        !loadingMore &&
        !loadingCampaigns &&
        !isRequestingRef.current

      // Debug: Log cuando se detecta scroll pero no se puede cargar
      if (scrollPercentage >= 0.85 && hasValidNextKey && !canLoadMore) {
        console.log('[Campaigns] ⚠️ Scroll al 85% pero bloqueado por flags:', {
          scrollPercentage: (scrollPercentage * 100).toFixed(1) + '%',
          hasMore,
          hasValidNextKey,
          nextKey: nextKey ? nextKey.substring(0, 30) + '...' : null,
          loadingMore,
          loadingCampaigns,
          isRequesting: isRequestingRef.current,
          canLoadMore
        })
      }

      if (scrollPercentage >= 0.85 && canLoadMore) {
        console.log(
          '[Campaigns] 📜 Scroll al 85% detectado, intentando cargar más campañas...',
          {
            scrollPercentage: (scrollPercentage * 100).toFixed(1) + '%',
            hasMore,
            hasNextKey: !!nextKey,
            nextKey: nextKey ? nextKey.substring(0, 30) + '...' : null,
            loadingMore,
            loadingCampaigns,
            isRequesting: isRequestingRef.current,
            canLoadMore
          }
        )

        // Guardar la posición del scroll ANTES de cargar más contenido
        // Esto se usará para mantener la posición después de que React actualice el DOM
        scrollPositionRef.current =
          window.pageYOffset || document.documentElement.scrollTop
        isRestoringScrollRef.current = true

        // Cargar más campañas (listCampaigns establecerá el flag isRequestingRef internamente)
        // El backend decidirá si hay más contenido o no
        listCampaigns(false)
          .then(() => {
            console.log('[Campaigns] ✅ Carga completada')
            // Verificar que el flag se haya reseteado
            setTimeout(() => {
              if (isRequestingRef.current) {
                console.warn(
                  '[Campaigns] ⚠️ Flag isRequesting aún en true después de carga - reseteando'
                )
                isRequestingRef.current = false
              }
            }, 200)
          })
          .catch((err) => {
            console.error('[Campaigns] ❌ Error:', err)
            // En caso de error, resetear el flag para permitir reintentos
            isRequestingRef.current = false
            scrollPositionRef.current = null
            isRestoringScrollRef.current = false
          })
      } else if (
        scrollPercentage >= 0.85 &&
        hasValidNextKey &&
        (loadingMore || loadingCampaigns || isRequestingRef.current)
      ) {
        // Si hay nextKey pero está cargando, solo loguear (no hacer nada)
        console.log(
          '[Campaigns] ⏳ Scroll detectado pero ya está cargando...',
          {
            loadingMore,
            loadingCampaigns,
            isRequesting: isRequestingRef.current
          }
        )
      }
    }

    // Agregar el event listener con throttle para mejorar el rendimiento
    let ticking = false
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledHandleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMore,
    nextKey,
    loadingMore,
    loadingCampaigns,
    groupByTemplate,
    filterText
  ])

  // Debug: Log cuando cambian los estados de paginación
  useEffect(() => {
    console.log('[Campaigns] 🔍 Estado de paginación actualizado:', {
      hasMore,
      hasNextKey: !!nextKey,
      nextKey: nextKey ? nextKey.substring(0, 30) + '...' : null,
      loadingMore,
      loadingCampaigns,
      isRequesting: isRequestingRef.current
    })

    // Verificación de seguridad: si el flag está bloqueado pero no está cargando, resetearlo
    if (isRequestingRef.current && !loadingMore && !loadingCampaigns) {
      // Esperar un poco para asegurarse de que no está en proceso de carga
      const timeoutId = setTimeout(() => {
        if (isRequestingRef.current && !loadingMore && !loadingCampaigns) {
          console.warn(
            '[Campaigns] ⚠️ Flag isRequesting bloqueado incorrectamente - reseteando'
          )
          isRequestingRef.current = false
        }
      }, 500)

      return () => clearTimeout(timeoutId)
    }
  }, [hasMore, nextKey, loadingMore, loadingCampaigns])

  // useLayoutEffect se ejecuta ANTES de que el navegador pinte
  // Esto permite restaurar el scroll antes de que el usuario vea el movimiento
  useLayoutEffect(() => {
    // Solo restaurar cuando realmente estamos cargando más contenido (paginación)
    if (
      isRestoringScrollRef.current &&
      scrollPositionRef.current !== null &&
      !loadingMore &&
      !loadingCampaigns
    ) {
      const savedScrollTop = scrollPositionRef.current

      // Restaurar el scroll inmediatamente antes de que el navegador pinte
      // Esto previene que el usuario vea el movimiento
      document.documentElement.scrollTop = savedScrollTop
      document.body.scrollTop = savedScrollTop // Para compatibilidad con navegadores antiguos

      // Limpiar las referencias
      scrollPositionRef.current = null
      isRestoringScrollRef.current = false
    }
  }, [
    groupedCampaignsData,
    groupedScheduledData,
    campaigns,
    scheduled,
    loadingMore,
    loadingCampaigns
  ])

  useEffect(() => {
    // Cargar campañas inicialmente
    console.log('[Campaigns] 🚀 Iniciando carga inicial de campañas')
    listCampaigns(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Recargar cuando cambie el modo de agrupación
  useEffect(() => {
    // Recargar si hay datos cargados (en cualquier formato) o si es la primera vez
    const hasData =
      allCampaigns.length > 0 ||
      campaigns.length > 0 ||
      scheduled.length > 0 ||
      (groupedCampaignsData && groupedCampaignsData.length > 0) ||
      (groupedScheduledData && groupedScheduledData.length > 0)

    if (hasData) {
      // Recargar con el nuevo modo de vista
      console.log('[Campaigns] 🔄 Cambiando modo de vista, recargando campañas...', {
        groupByTemplate,
        hasData
      })
      listCampaigns(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByTemplate])

  // Efecto para manejar el filtro con debounce
  useEffect(() => {
    // Limpiar timer anterior si existe
    if (filterDebounceTimer) {
      clearTimeout(filterDebounceTimer)
    }

    // Si el filtro está vacío inmediatamente, recargar con delay corto
    // Si hay texto, esperar 400ms para evitar demasiadas llamadas mientras se escribe
    const trimmedFilter = filterText ? filterText.trim() : ''
    const delay = trimmedFilter ? 400 : 150 // Delay corto cuando está vacío para recargar rápido

    const timer = setTimeout(() => {
      const currentFilter = filterText ? filterText.trim() : ''
      console.log('[Campaigns] 🔍 Filtro cambiado, recargando campañas...', {
        filterText: currentFilter || '(vacío - mostrar todas)',
        delay,
        willReset: true
      })

      // Resetear estados de paginación y datos cuando cambia el filtro
      // NO mostrar loading spinner para evitar que parezca un refresh de página
      setNextKey(null)
      setHasMore(true) // Resetear hasMore para permitir carga inicial
      // Limpiar datos existentes suavemente (sin mostrar spinner)
      setAllCampaigns([])
      setCampaigns([])
      setScheduled([])
      setGroupedCampaignsData(null)
      setGroupedScheduledData(null)
      isRequestingRef.current = false // Resetear flag de request
      // NO establecer loadingCampaigns aquí - solo se establecerá dentro de listCampaigns si es necesario

      // Llamar a listCampaigns con el filtro actual (puede estar vacío)
      // NO usar setTimeout adicional para evitar delays innecesarios
      listCampaigns(true)
    }, delay)

    setFilterDebounceTimer(timer)

    // Cleanup
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText])

  return (
    <>
      {/* Solo mostrar spinner de loading en la carga inicial real, no cuando cambia el filtro */}
      {loadingCampaigns &&
      (!filterText || !filterText.trim()) &&
      allCampaigns.length === 0 &&
      campaigns.length === 0 &&
      scheduled.length === 0 &&
      (!groupedCampaignsData || groupedCampaignsData.length === 0) &&
      (!groupedScheduledData || groupedScheduledData.length === 0) ? (
        <div className="flex justify-center my-16">
          <Spin type="lightBg" />
        </div>
      ) : (
        // Siempre mostrar el contenido, incluso si está cargando más
        // El contenido existente se mantiene visible mientras se carga más
        <div className="pb-24 -mt-10">
          {/* Contenedor sticky para filtros y controles */}
          <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200/50 shadow-lg mb-6 py-3 space-y-3">
            {/* Fila inferior: Filtros de tipo y vista */}
            <div className="flex items-center justify-between gap-4">
              {/* Filtros de tipo de campaña (All, Launched, Scheduled) */}
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setCampaignTypeFilter('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    campaignTypeFilter === 'all'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {i18n.t('all') || 'Todas'}
                </button>
                <button
                  type="button"
                  onClick={() => setCampaignTypeFilter('launched')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    campaignTypeFilter === 'launched'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {i18n.t('already_sent') || 'Ya Enviadas'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCampaignTypeFilter('scheduled')
                    loadSchedules()
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    campaignTypeFilter === 'scheduled'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {i18n.t('scheduled_campaigns') || 'Campañas Programadas'}
                </button>
              </div>

              {/* Botones de vista (Lista / Agrupadas) */}
              <div className="flex items-center gap-2">
                {/* Estadísticas globales: agrega todas las campañas del user
                    en un rango de fechas (cruza flujos). */}
                <button
                  type="button"
                  onClick={() => history.push('/statistics?global=1')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 19v-6a1 1 0 00-1-1H5a1 1 0 00-1 1v6a1 1 0 001 1h3a1 1 0 001-1zm0 0V9a1 1 0 011-1h3a1 1 0 011 1v10m-5 0h5m4 0h-3a1 1 0 01-1-1V5a1 1 0 011-1h3a1 1 0 011 1v13a1 1 0 01-1 1z"
                    />
                  </svg>
                  {i18n.t('global_stats') || 'Estadísticas globales'}
                </button>
                <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setGroupByTemplate(false)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                      !groupByTemplate
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {i18n.t('list') || 'Lista'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setGroupByTemplate(true)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center gap-2 ${
                      groupByTemplate
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                    {i18n.t('grouped') || 'Agrupadas'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contenido de campañas filtrado por tipo */}
          <div className="mt-2">
            {/* Estado vacío - No hay ninguna campaña (solo mostrar cuando no hay nada en ninguna categoría) */}
            {!loadingCampaigns &&
              !loadingMore &&
              campaigns.length === 0 &&
              scheduled.length === 0 &&
              (!groupedCampaignsData || groupedCampaignsData.length === 0) &&
              (!groupedScheduledData || groupedScheduledData.length === 0) ? (
                <div className="text-center py-16 px-4">
                  <div className="mx-auto w-24 h-24 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                    <DocumentTextIcon className="h-12 w-12 text-indigo-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {i18n.t('no_campaigns_yet') || 'No hay campañas todavía'}
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {i18n.t('no_campaigns_description') || 'Crea tu primera campaña para empezar a enviar mensajes a tus contactos.'}
                  </p>
                </div>
              ) : (
              <>
            {/* Mostrar campañas según el filtro seleccionado */}
            {campaignTypeFilter === 'all' ||
            campaignTypeFilter === 'scheduled' ? (
              <>
                {/* Campañas programadas REALES (items de prod-scheduled) */}
                {realSchedules.length > 0 && (
                  <div className="mb-6 space-y-3">
                    {realSchedules.map((sc) => (
                      <div
                        key={sc.scheduleId}
                        className="flex items-center justify-between bg-white border border-blue-200 rounded-lg p-4"
                      >
                        <div className="flex items-center gap-3">
                          <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5">
                            {sc.type === 'excel' ? 'Excel' : 'Campaña'}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {sc.flow || '—'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Programada:{' '}
                              {sc.scheduleDateTime
                                ? new Date(
                                    parseInt(sc.scheduleDateTime)
                                  ).toLocaleString()
                                : '—'}
                              {typeof sc.recipients === 'number'
                                ? ` · ${sc.recipients} contactos`
                                : ''}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={cancelingSchedule === sc.scheduleId}
                          onClick={() => cancelSchedule(sc.scheduleId)}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                        >
                          {cancelingSchedule === sc.scheduleId
                            ? 'Cancelando…'
                            : 'Cancelar'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Verificar si hay campañas programadas: en modo agrupado verificar groupedScheduledData, en modo lista verificar scheduled */}
                {(campaignTypeFilter === 'all' ||
                  campaignTypeFilter === 'scheduled') &&
                realSchedules.length === 0 &&
                ((groupByTemplate &&
                  (!groupedScheduledData ||
                    groupedScheduledData.length === 0)) ||
                  (!groupByTemplate && scheduled.length === 0)) &&
                !loadingCampaigns &&
                !loadingMore ? (
                  campaignTypeFilter === 'scheduled' ? (
                    <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <p className="text-lg font-medium mb-2">
                        {i18n.t('no_campaigns_scheduled') ||
                          'No hay campañas programadas'}
                      </p>
                      <p className="text-sm text-gray-400">
                        Las campañas programadas aparecerán aquí
                      </p>
                    </div>
                  ) : null
                ) : groupByTemplate &&
                  groupedScheduledData &&
                  groupedScheduledData.length > 0 ? (
                  // Vista agrupada para programadas
                  <>
                    {groupedScheduledData.map((group) => {
                      const isCollapsed = collapsedTemplates.has(
                        group.template.name
                      )
                      return (
                        <div key={group.template.name} className="mb-6">
                          {/* Header del template - clickeable para colapsar */}
                          <div className="relative mb-3">
                          <button
                            type="button"
                            onClick={() =>
                              toggleTemplateCollapse(group.template.name)
                            }
                            className="w-full bg-gradient-to-r from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-lg p-5 hover:from-indigo-100 hover:to-indigo-200 transition-all duration-200 shadow-sm hover:shadow-md"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 text-left">
                                <div className="flex items-center gap-3 mb-2">
                                  <DocumentTextIcon className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                                  <h3 className="text-xl font-bold text-indigo-900">
                                    {group.template.name}
                                  </h3>
                                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-200 text-indigo-800">
                                    {group.campaigns.length}{' '}
                                    {group.campaigns.length === 1
                                      ? 'campaña'
                                      : 'campañas'}
                                  </span>
                                </div>
                                {group.template.previewText && (
                                  <p className="text-sm text-gray-700 mt-2 ml-9">
                                    {group.template.previewText}
                                  </p>
                                )}
                                {group.template.sender && (
                                  <p className="text-xs text-gray-600 mt-1 ml-9">
                                    Remitente: {group.template.sender}
                                  </p>
                                )}
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                {isCollapsed ? (
                                  <ChevronDownIcon className="h-6 w-6 text-indigo-600" />
                                ) : (
                                  <ChevronUpIcon className="h-6 w-6 text-indigo-600" />
                                )}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            title="Ver estadísticas agregadas de la template"
                            onClick={(e) => {
                              e.stopPropagation()
                              goToTemplateStats(group.template.name)
                            }}
                            className="absolute top-1/2 -translate-y-1/2 right-14 flex items-center justify-center w-10 h-10 rounded-full bg-white/80 text-indigo-700 hover:bg-white hover:text-indigo-900 shadow-sm transition-colors"
                          >
                            <ChartPieIcon className="h-6 w-6" />
                          </button>
                          </div>

                          {/* Campañas dentro del template - colapsable */}
                          {!isCollapsed && (
                            <div className="space-y-3 ml-4 animate-fadeIn">
                              {group.campaigns.map((campaign) => (
                                <div
                                  key={campaign.campaignId}
                                  className="border-gray-300 relative block bg-white border rounded-lg shadow-sm px-6 py-4 sm:flex sm:justify-between focus:outline-none"
                                >
                                  <div className="flex-1 flex items-center gap-4 flex-wrap">
                                    <span className="flex items-center min-w-[200px]">
                                      <span className="text-sm flex flex-col">
                                        <div className="font-medium text-grey-blue">
                                          {campaign.flow || campaign.campaignId}
                                        </div>
                                        {campaign.timestamp && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            {
                                              dateToString(campaign.timestamp)
                                                .full
                                            }
                                          </div>
                                        )}
                                      </span>
                                    </span>

                                    <div className="flex items-center gap-2 text-sm">
                                      <UsersIcon className="h-4 w-4 text-gray-400" />
                                      <span className="text-gray-600">
                                        {campaign.contactsCount !== undefined
                                          ? `${campaign.contactsCount} contactos`
                                          : 'N/A'}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="font-medium text-grey-blue">
                                        {i18n.t('status')}:
                                      </span>
                                      <span
                                        className={
                                          (campaign.status === 'open'
                                            ? 'text-green-500'
                                            : 'text-red-500') +
                                          ' font-medium capitalize'
                                        }
                                      >
                                        {campaign.status === 'close'
                                          ? 'closed'
                                          : campaign.status}
                                      </span>
                                    </div>

                                    <CampaignActionButtons
                                      campaign={campaign}
                                      history={history}
                                      openContactsModal={openContactsModal}
                                      goToCampaignStats={goToCampaignStats}
                                      closeCampaign={closeCampaign}
                                      deleteCampaign={deleteCampaign}
                                      closingCampaignMap={closingCampaignMap}
                                      deletingCampaignMap={deletingCampaignMap}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </>
                ) : (
                  // Vista lista para programadas
                  scheduled.map((campaign) => (
                    <div
                      key={campaign.campaignId}
                      value={campaign}
                      className={
                        'border-gray-300 mb-5 relative block bg-white border rounded-lg shadow-sm px-6 py-4 sm:flex sm:justify-between focus:outline-none'
                      }
                    >
                      <div className="flex-1 flex items-center gap-4 flex-wrap">
                        <span className="flex items-center min-w-[200px]">
                          <span className="text-sm flex flex-col">
                            <div className="font-medium text-grey-blue">
                              {campaign.flow || campaign.campaignId}
                            </div>
                            {campaign.template && (
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <DocumentTextIcon className="h-3 w-3" />
                                Template: {campaign.template.name}
                              </div>
                            )}
                          </span>
                        </span>

                        {campaign.contactsCount !== undefined && (
                          <div className="flex items-center gap-2 text-sm">
                            <UsersIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">
                              {campaign.contactsCount} contactos
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-grey-blue">
                            {i18n.t('status')}:
                          </span>
                          <span
                            className={
                              (campaign.status === 'open'
                                ? 'text-green-500'
                                : 'text-red-500') + ' font-medium capitalize'
                            }
                          >
                            {campaign.status === 'close'
                              ? 'closed'
                              : campaign.status}
                          </span>
                        </div>

                        <div className="flex items-center text-sm">
                          <span className="font-medium text-grey-blue">
                            {campaign.timestamp
                              ? dateToString(campaign.timestamp).full
                              : 'N/A'}
                          </span>
                        </div>

                        <CampaignActionButtons
                          campaign={campaign}
                          history={history}
                          openContactsModal={openContactsModal}
                          goToCampaignStats={goToCampaignStats}
                          closeCampaign={closeCampaign}
                          deleteCampaign={deleteCampaign}
                          closingCampaignMap={closingCampaignMap}
                          deletingCampaignMap={deletingCampaignMap}
                        />
                      </div>
                    </div>
                  ))
                )}
              </>
            ) : null}

            {/* Campañas lanzadas */}
            {(campaignTypeFilter === 'all' ||
              campaignTypeFilter === 'launched') && (
              <>
                {/* Verificar si hay campañas: en modo agrupado verificar groupedCampaignsData, en modo lista verificar campaigns */}
                {((groupByTemplate &&
                  (!groupedCampaignsData ||
                    groupedCampaignsData.length === 0)) ||
                  (!groupByTemplate && campaigns.length === 0)) &&
                !loadingCampaigns &&
                !loadingMore ? (
                  campaignTypeFilter === 'launched' ? (
                    <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <p className="text-lg font-medium mb-2">
                        {i18n.t('no_campaigns_launched') ||
                          'No hay campañas lanzadas'}
                      </p>
                      <p className="text-sm text-gray-400">
                        Las campañas lanzadas aparecerán aquí
                      </p>
                    </div>
                  ) : null
                ) : groupByTemplate &&
                  groupedCampaignsData &&
                  groupedCampaignsData.length > 0 ? (
                  // Vista agrupada por template (datos del backend)
                  <>
                    {groupedCampaignsData.map((group) => {
                      const isCollapsed = collapsedTemplates.has(
                        group.template.name
                      )
                      return (
                        <div key={group.template.name} className="mb-6">
                          {/* Header del template - clickeable para colapsar */}
                          <div className="relative mb-3">
                          <button
                            type="button"
                            onClick={() =>
                              toggleTemplateCollapse(group.template.name)
                            }
                            className="w-full bg-gradient-to-r from-indigo-50 to-indigo-100 border-2 border-indigo-200 rounded-lg p-5 hover:from-indigo-100 hover:to-indigo-200 transition-all duration-200 shadow-sm hover:shadow-md"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 text-left">
                                <div className="flex items-center gap-3 mb-2">
                                  <DocumentTextIcon className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                                  <h3 className="text-xl font-bold text-indigo-900">
                                    {group.template.name}
                                  </h3>
                                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-indigo-200 text-indigo-800">
                                    {group.campaigns.length}{' '}
                                    {group.campaigns.length === 1
                                      ? 'campaña'
                                      : 'campañas'}
                                  </span>
                                </div>
                                {group.template.previewText && (
                                  <p className="text-sm text-gray-700 mt-2 ml-9">
                                    {group.template.previewText}
                                  </p>
                                )}
                                {group.template.sender && (
                                  <p className="text-xs text-gray-600 mt-1 ml-9">
                                    Remitente: {group.template.sender}
                                  </p>
                                )}
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                {isCollapsed ? (
                                  <ChevronDownIcon className="h-6 w-6 text-indigo-600" />
                                ) : (
                                  <ChevronUpIcon className="h-6 w-6 text-indigo-600" />
                                )}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            title="Ver estadísticas agregadas de la template"
                            onClick={(e) => {
                              e.stopPropagation()
                              goToTemplateStats(group.template.name)
                            }}
                            className="absolute top-1/2 -translate-y-1/2 right-14 flex items-center justify-center w-10 h-10 rounded-full bg-white/80 text-indigo-700 hover:bg-white hover:text-indigo-900 shadow-sm transition-colors"
                          >
                            <ChartPieIcon className="h-6 w-6" />
                          </button>
                          </div>

                          {/* Campañas dentro del template - colapsable */}
                          {!isCollapsed && (
                            <div className="space-y-3 ml-4 animate-fadeIn">
                              {group.campaigns.map((campaign) => (
                                <div
                                  key={campaign.campaignId}
                                  className="border-gray-300 relative block bg-white border rounded-lg shadow-sm px-6 py-4 sm:flex sm:justify-between focus:outline-none"
                                >
                                  <div className="flex-1 flex items-center gap-4 flex-wrap">
                                    <span className="flex items-center min-w-[200px]">
                                      <span className="text-sm flex flex-col">
                                        <div className="font-medium text-grey-blue">
                                          {campaign.flow || campaign.campaignId}
                                        </div>
                                        {campaign.timestamp && (
                                          <div className="text-xs text-gray-500 mt-1">
                                            {
                                              dateToString(campaign.timestamp)
                                                .full
                                            }
                                          </div>
                                        )}
                                      </span>
                                    </span>

                                    <div className="flex items-center gap-2 text-sm">
                                      <UsersIcon className="h-4 w-4 text-gray-400" />
                                      <span className="text-gray-600">
                                        {campaign.contactsCount !== undefined
                                          ? `${campaign.contactsCount} contactos`
                                          : 'N/A'}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="font-medium text-grey-blue">
                                        {i18n.t('status')}:
                                      </span>
                                      <span
                                        className={
                                          (campaign.status === 'open'
                                            ? 'text-green-500'
                                            : 'text-red-500') +
                                          ' font-medium capitalize'
                                        }
                                      >
                                        {campaign.status === 'close'
                                          ? 'closed'
                                          : campaign.status}
                                      </span>
                                    </div>

                                    <CampaignActionButtons
                                      campaign={campaign}
                                      history={history}
                                      openContactsModal={openContactsModal}
                                      goToCampaignStats={goToCampaignStats}
                                      closeCampaign={closeCampaign}
                                      deleteCampaign={deleteCampaign}
                                      closingCampaignMap={closingCampaignMap}
                                      deletingCampaignMap={deletingCampaignMap}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </>
                ) : (
                  // Vista lista para lanzadas
                  campaigns.map((campaign) => (
                    <div
                      key={campaign.campaignId}
                      value={campaign}
                      className={
                        'border-gray-300 mb-5 relative block bg-white border rounded-lg shadow-sm px-6 py-4 sm:flex sm:justify-between focus:outline-none'
                      }
                    >
                      <div className="flex-1 flex items-center gap-4 flex-wrap">
                        <span className="flex items-center min-w-[200px]">
                          <span className="text-sm flex flex-col">
                            <div className="font-medium text-grey-blue">
                              {campaign.flow || campaign.campaignId}
                            </div>
                            {campaign.template && (
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <DocumentTextIcon className="h-3 w-3" />
                                Template: {campaign.template.name}
                              </div>
                            )}
                          </span>
                        </span>

                        {campaign.contactsCount !== undefined && (
                          <div className="flex items-center gap-2 text-sm">
                            <UsersIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">
                              {campaign.contactsCount} contactos
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-grey-blue">
                            {i18n.t('status')}:
                          </span>
                          <span
                            className={
                              (campaign.status === 'open'
                                ? 'text-green-500'
                                : 'text-red-500') + ' font-medium capitalize'
                            }
                          >
                            {campaign.status === 'close'
                              ? 'closed'
                              : campaign.status}
                          </span>
                        </div>

                        <div className="flex items-center text-sm">
                          <span className="font-medium text-grey-blue">
                            {campaign.timestamp
                              ? dateToString(campaign.timestamp).full
                              : 'N/A'}
                          </span>
                        </div>

                        <CampaignActionButtons
                          campaign={campaign}
                          history={history}
                          openContactsModal={openContactsModal}
                          goToCampaignStats={goToCampaignStats}
                          closeCampaign={closeCampaign}
                          deleteCampaign={deleteCampaign}
                          closingCampaignMap={closingCampaignMap}
                          deletingCampaignMap={deletingCampaignMap}
                        />
                      </div>
                    </div>
                  ))
                )}

                {/* Loader cuando está cargando más */}
                {loadingMore && (
                  <div className="flex justify-center items-center py-4">
                    <Spin type="lightBg" />
                    <span className="ml-2 text-gray-500 text-sm">
                      {i18n.t('loading_more') || 'Cargando más campañas...'}
                    </span>
                  </div>
                )}

              </>
            )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de contactos */}
      <Transition.Root show={contactsModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={setContactsModalOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl sm:p-6">
                  <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setContactsModalOpen(false)}
                    >
                      <span className="sr-only">Cerrar</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <UserGroupIcon
                        className="h-6 w-6 text-blue-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {i18n.t('campaign_contacts') ||
                          'Contactos de la campaña'}
                      </Dialog.Title>
                      <div className="mt-4">
                        {loadingContacts ? (
                          <div className="flex justify-center items-center py-8">
                            <Spin type="lightBg" />
                            <span className="ml-2 text-gray-500">
                              {i18n.t('loading_contacts') ||
                                'Cargando contactos...'}
                            </span>
                          </div>
                        ) : campaignContacts.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            {i18n.t('no_contacts') ||
                              'No hay contactos en esta campaña'}
                          </div>
                        ) : (
                          <div className="max-h-96 overflow-y-auto">
                            <div className="mb-3 text-sm text-gray-600">
                              {i18n.t('total_contacts') || 'Total'}:{' '}
                              <span className="font-semibold text-gray-900">
                                {campaignContacts.length}
                              </span>
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              {/* Tabla de contactos */}
                              <div className="w-full overflow-x-auto">
                                <table className="min-w-[920px] divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      #
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('phone') || 'Teléfono'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('name') || 'Nombre'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('email') || 'Email'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('status') || 'Estado'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('flow_status') || 'Estado flujo'}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      {i18n.t('action') || 'Acción'}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {campaignContacts.map((contact, index) => {
                                    const phone =
                                      typeof contact === 'string'
                                        ? contact
                                        : contact.phone
                                    const name =
                                      contact.name ||
                                      contact.extraData?.name ||
                                      '-'
                                    const email =
                                      contact.email ||
                                      contact.extraData?.email ||
                                      '-'
                                    const flowClosed = !!contact.flowClosed
                                    const flowCompleted = !!contact.flowCompleted
                                    const canCloseFlow =
                                      contact.canCloseFlow !== undefined
                                        ? !!contact.canCloseFlow
                                        : !flowCompleted
                                    const isUpdatingFlow = !!updatingFlowMap[phone]
                                    const status = contact.delivered
                                      ? 'Entregado'
                                      : contact.sent
                                      ? 'Enviado'
                                      : contact.failed
                                      ? 'Fallido'
                                      : contact.hasDelivery
                                      ? 'Procesado'
                                      : 'Pendiente'

                                    return (
                                      <tr
                                        key={index}
                                        className="hover:bg-gray-50 transition-colors"
                                      >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                            <span className="text-xs font-medium text-indigo-600">
                                              {index + 1}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <p className="text-sm font-medium text-gray-900">
                                            {phone}
                                          </p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <p className="text-sm text-gray-600">
                                            {name}
                                          </p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <p className="text-sm text-gray-600">
                                            {email}
                                          </p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span
                                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-default ${
                                              contact.delivered
                                                ? 'bg-green-100 text-green-800'
                                                : contact.sent
                                                ? 'bg-blue-100 text-blue-800'
                                                : contact.failed
                                                ? 'bg-red-100 text-red-800'
                                                : contact.hasDelivery
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                            }`}
                                            title={contact.failed && contact.error ? contact.error : ''}
                                          >
                                            {status}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span
                                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-default ${
                                              flowClosed
                                                ? 'bg-red-100 text-red-800'
                                                : flowCompleted
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-amber-100 text-amber-800'
                                            }`}
                                          >
                                            {flowClosed
                                              ? i18n.t('flow_closed_manual') ||
                                                'Cerrado manualmente'
                                              : flowCompleted
                                              ? i18n.t('flow_completed') ||
                                                'Terminado'
                                              : i18n.t('flow_in_progress') ||
                                                'En curso'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          {flowClosed ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateContactFlowStatus(
                                                  phone,
                                                  false,
                                                  selectedCampaignId
                                                )
                                              }
                                              disabled={isUpdatingFlow}
                                              className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                            >
                                              {isUpdatingFlow
                                                ? i18n.t('processing') ||
                                                  'Procesando...'
                                                : i18n.t('reopen_flow') ||
                                                  'Reabrir'}
                                            </button>
                                          ) : canCloseFlow ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateContactFlowStatus(
                                                  phone,
                                                  true,
                                                  selectedCampaignId
                                                )
                                              }
                                              disabled={isUpdatingFlow}
                                              className="inline-flex items-center rounded-md bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                            >
                                              {isUpdatingFlow
                                                ? i18n.t('processing') ||
                                                  'Procesando...'
                                                : i18n.t('close_flow') ||
                                                  'Cerrar flujo'}
                                            </button>
                                          ) : (
                                            <span className="text-xs text-gray-400">
                                              {i18n.t('no_actions') || '-'}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => setContactsModalOpen(false)}
                    >
                      {i18n.t('close') || 'Cerrar'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Componente genérico de búsqueda y crear */}
      <SearchAndCreateButton
        filterText={filterText}
        setFilterText={setFilterText}
        createRoute="/campaign"
        searchPlaceholder={
          i18n.t('filter_campaigns') ||
          'Filtrar campañas por nombre, template...'
        }
        createButtonTitle={i18n.t('create_campaign') || 'Create Campaign'}
      />

      {/* Modal de confirmación de borrado (soft-delete) */}
      <Transition.Root show={!!campaignPendingDelete} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-20"
          onClose={() => setCampaignPendingDelete(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                      <TrashIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Eliminar campaña
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          ¿Quieres eliminar la campaña{' '}
                          <strong>
                            {campaignPendingDelete?.campaignName ||
                              campaignPendingDelete?.flow ||
                              campaignPendingDelete?.campaignId ||
                              ''}
                          </strong>
                          ? Si está abierta, se cerrará antes de eliminarla.
                          Puedes recuperarla desde soporte si te arrepientes.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 sm:col-start-2"
                      onClick={confirmDeleteCampaign}
                    >
                      Eliminar
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => setCampaignPendingDelete(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default Campaigns
